/**
 * Lit un fichier CSV ou Excel (.xlsx) et rend ses lignes en objets
 * { en-tête: valeur }. Le serveur décide ensuite quelles colonnes il
 * reconnaît : ici on ne fait que lire.
 *
 * Excel passe par read-excel-file, chargé seulement quand on en a besoin.
 */

export type LigneTableur = Record<string, string>

export class FichierIllisible extends Error {}

const EXTENSIONS_EXCEL = ['.xlsx']

export function estExcel(fichier: File): boolean {
  const nom = fichier.name.toLowerCase()
  return EXTENSIONS_EXCEL.some((ext) => nom.endsWith(ext))
}

export async function lireTableur(fichier: File): Promise<LigneTableur[]> {
  const grille = estExcel(fichier) ? await lireExcel(fichier) : lireCsv(await lireTexte(fichier))
  return enObjets(grille)
}

async function lireExcel(fichier: File): Promise<string[][]> {
  const { readSheet } = await import('read-excel-file/browser')
  try {
    const lignes = await readSheet(fichier)
    return lignes.map((ligne) =>
      ligne.map((cellule) => {
        if (cellule === null || cellule === undefined) return ''
        if (cellule instanceof Date) return cellule.toISOString().slice(0, 10)
        return String(cellule)
      }),
    )
  } catch {
    throw new FichierIllisible()
  }
}

/** UTF-8 d'abord ; si le fichier vient d'un vieil Excel (Windows-1252), on relit. */
async function lireTexte(fichier: File): Promise<string> {
  const octets = await fichier.arrayBuffer()
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(octets)
  } catch {
    return new TextDecoder('windows-1252').decode(octets)
  }
}

/**
 * CSV avec guillemets (une valeur peut contenir le séparateur ou un retour à
 * la ligne). Séparateur deviné sur la première ligne : virgule, point-virgule
 * (Excel en français) ou tabulation.
 */
export function lireCsv(texte: string): string[][] {
  const contenu = texte.replace(/^﻿/, '')
  const premiere = contenu.split(/\r?\n/, 1)[0] ?? ''
  const compte = (c: string) => premiere.split(c).length - 1
  const separateur = [';', '\t'].reduce((choix, c) => (compte(c) > compte(choix) ? c : choix), ',')

  const lignes: string[][] = []
  let ligne: string[] = []
  let valeur = ''
  let entreGuillemets = false

  for (let i = 0; i < contenu.length; i++) {
    const c = contenu[i]
    if (entreGuillemets) {
      if (c === '"' && contenu[i + 1] === '"') {
        valeur += '"'
        i++
      } else if (c === '"') {
        entreGuillemets = false
      } else {
        valeur += c
      }
    } else if (c === '"') {
      entreGuillemets = true
    } else if (c === separateur) {
      ligne.push(valeur)
      valeur = ''
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && contenu[i + 1] === '\n') i++
      ligne.push(valeur)
      lignes.push(ligne)
      ligne = []
      valeur = ''
    } else {
      valeur += c
    }
  }
  if (valeur !== '' || ligne.length) {
    ligne.push(valeur)
    lignes.push(ligne)
  }
  return lignes
}

function enObjets(grille: string[][]): LigneTableur[] {
  const [entetes, ...reste] = grille
  if (!entetes) return []
  const noms = entetes.map((e) => e.trim())
  return reste
    .filter((ligne) => ligne.some((v) => v.trim() !== ''))
    .map((ligne) => {
      const objet: LigneTableur = {}
      noms.forEach((nom, i) => {
        if (nom) objet[nom] = (ligne[i] ?? '').trim()
      })
      return objet
    })
}
